import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Chip, CircularProgress, InputAdornment, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { comptoirMatieresAPI } from '../../../services/api';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { ComptoirStockMatiere as StockMatiere } from '../../../types/comptoir';
import { COLOR, num } from '../ui';

/** Comptoir › Stock matière (prototype V8 « Stock »): what the open services need from the ERP
 *  stock — reserved, available, missing — and what must be bought. The stock itself (entrées,
 *  sorties, inventaire) stays in the Stock module. */
const ComptoirStockMatiere: React.FC = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const { data, isLoading } = useQuery<StockMatiere>({ queryKey: ['comptoir', 'matieres-stock'], queryFn: () => comptoirMatieresAPI.stock(), staleTime: 0 });
  const t = q.trim().toLowerCase();
  const articles = (data?.articles || []).filter((a) => !t || `${a.reference} ${a.designation} ${a.services.join(' ')}`.toLowerCase().includes(t));
  const manques = (data?.articles || []).filter((a) => num(a.manque) > 0);
  const besoins = (data?.a_acheter.length || 0) + manques.length;
  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Stock matière</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Une offre ne touche pas au stock : la quantité est réservée quand la production l'ajoute au service, puis déduite à la
        « Sortie du stock » depuis la fiche du service. La matière du client n'entre jamais ici.
      </Typography>
      {isLoading ? <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: COLOR }} /></Box> : (
        <>
          <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: besoins ? 1 : 0, borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Besoins d'achat</Typography>
              <Chip size="small" label={besoins} color={besoins ? 'error' : 'default'} />
            </Stack>
            {!besoins && <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1.5 }}>Rien à acheter pour les services en cours.</Typography>}
            {data?.a_acheter.map((b) => (
              <Stack key={`h-${b.id}`} direction="row" alignItems="center" spacing={1.5} onClick={() => navigate(`/comptoir/services/${b.service_id}`)}
                sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.designation} · {num(b.quantite)} {b.unite || ''}</Typography>
                  <Typography variant="caption" color="text.secondary">Hors stock · {b.service}{b.client ? ` · ${b.client}` : ''}</Typography>
                </Box>
                <Chip size="small" label="À acheter" color="error" variant="outlined" />
              </Stack>
            ))}
            {manques.map((a) => (
              <Stack key={`m-${a.article_id}`} direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.designation} · manque {num(a.manque)} {a.unite || ''}</Typography>
                  <Typography variant="caption" color="text.secondary">En stock {num(a.en_stock)} · réservé pour {a.services.join(', ')}</Typography>
                </Box>
                <Chip size="small" label="Entrée de stock à faire" color="warning" variant="outlined" />
              </Stack>
            ))}
          </Paper>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Articles réservés</Typography>
            <TextField size="small" placeholder="Réf., désignation, service…" value={q} onChange={(e) => setQ(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Stack>
          {!articles.length ? <Alert severity="info">Aucun article du stock n'est réservé par un service en cours.</Alert> : (
            <TableContainer component={Paper} sx={getModuleTableTheme('comptoir')}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Réf.</TableCell><TableCell>Désignation</TableCell><TableCell align="right">En stock</TableCell>
                    <TableCell align="right">Réservé</TableCell><TableCell align="right">Disponible</TableCell><TableCell>Réservé pour</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {articles.map((a) => (
                    <TableRow key={a.article_id} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{a.reference}</TableCell>
                      <TableCell>{a.designation}</TableCell>
                      <TableCell align="right">{num(a.en_stock)} {a.unite || ''}</TableCell>
                      <TableCell align="right">{num(a.reserve)}</TableCell>
                      <TableCell align="right" sx={{ color: num(a.disponible) < 0 ? 'error.main' : undefined, fontWeight: num(a.disponible) < 0 ? 700 : undefined }}>
                        {num(a.disponible)}{num(a.disponible) < 0 ? ' · à acheter' : ''}
                      </TableCell>
                      <TableCell>{a.services.join(', ')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default ComptoirStockMatiere;
